import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ContentChild,
  inject,
  input,
  isSignal,
  output,
  TemplateRef,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HeaderIconButton, MobileService } from '@vectronic/util';
import { TableSelectionService } from '../../services/table-selection.service';
import { DatatableConfig } from '../../types/datatable-configuration';
import { DatatableSearchComponent } from '../datatable-search/datatable-search.component';

@Component({
  selector: 'table-datatable-header',
  templateUrl: './datatable-header.component.html',
  styleUrls: ['./datatable-header.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatBadgeModule,
    MatMenuModule,
    DatatableSearchComponent,
  ],
})
export class DatatableHeaderComponent<T extends object> {
  tableSelectionService = inject(TableSelectionService);
  mobileService = inject(MobileService);
  isSignal = isSignal;

  datatableConfig = input<DatatableConfig<T>>();
  titleExtension = input<string>();
  dynamicTableTitle = input<string>(undefined);
  isLoadingCSV = input<boolean>();
  isCompact = input<boolean>();
  hasData = input(false);
  filtersState = input({ count: 0, isCollapsed: false });
  hoverState = computed(() => !this.filtersState()?.isCollapsed && !this.mobileService.isOn());

  columnSelectionChange = output<any>();
  searchFieldEdit = output<string>();
  editColumn = output();
  compactToggle = output();
  export = output();
  exportSelected = output();
  filterToggle = output<boolean>();

  searchConfig = computed(
    () => this.datatableConfig()?.search as Exclude<DatatableConfig<T>['search'], boolean>,
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private allButtons = computed(() => {
    if (!this.datatableConfig()) return { displayButtons: [], groupedButtons: [] };
    const {
      headerIconButtons,
      compact,
      csvExport,
      csvExportSelected,
      showCompactModeIcon,
      hideCompactModeIcon,
      disableEditColumns,
      filter,
    } = this.datatableConfig();

    const buttons = [...(headerIconButtons ?? [])];
    const hasInMobileDropdown = headerIconButtons?.some((button) => button.inMobileDropdown);
    const searchConfig = this.searchConfig();
    if (searchConfig?.internal || searchConfig?.signal) {
      buttons.unshift({ id: 'search', icon: 'search' });
    }

    if (this.hasData()) {
      if (csvExport)
        buttons.push({
          id: 'export-csv',
          icon: 'file_download',
          onClick: () => this.export.emit(),
          hoverText: 'Export as csv',
          inMobileDropdown: hasInMobileDropdown,
        });

      if (csvExportSelected)
        buttons.push({
          id: 'export-csv',
          icon: 'file_download',
          onClick: () => this.exportSelected.emit(),
          hoverText: 'Export selected',
          isDisabled: computed(() => !this.tableSelectionService.selectionSignal()?.length),
          disabledHoverText: 'Please select the items you want to export first',
          inMobileDropdown: hasInMobileDropdown,
        });
    }

    if ((showCompactModeIcon || compact) && !hideCompactModeIcon) {
      buttons.push({
        id: 'compact-mode',
        icon: this.isCompact() ? 'grid_off' : 'grid_on',
        hoverText: 'Toggle compact view',
        onClick: () => this.compactToggle.emit(),
        inMobileDropdown: hasInMobileDropdown,
      });
    }

    if (!disableEditColumns)
      buttons.push({
        id: 'edit-columns',
        icon: 'edit_square',
        hoverText: 'Edit columns',
        onClick: () => this.editColumn.emit(),
        inMobileDropdown: hasInMobileDropdown,
      });

    if (filter)
      buttons.push({
        id: 'filter',
        icon: 'filter_alt',
        hoverText: 'Toggle filters',
        badge: this.filtersState().count || undefined,
        onClick: () => this.filterToggle.emit(!this.filtersState().isCollapsed),
      });

    let displayButtons = buttons;
    let groupedButtons: HeaderIconButton[] = [];

    if (this.mobileService.isOn()) {
      const visibleButtons = displayButtons.filter(
        (button) => (isSignal(button.doHide) ? !button.doHide() : !button.doHide) !== false,
      );
      const buttonsToGroup = visibleButtons.filter((button) => Boolean(button.inMobileDropdown));

      if (buttonsToGroup.length) {
        groupedButtons = buttonsToGroup;
        displayButtons = displayButtons.filter((button) => !buttonsToGroup.includes(button));
        displayButtons.push({
          id: 'more-options',
          icon: 'more_vert',
          hoverText: 'More options',
          orderIndex: 0,
        });
      }
    }

    const moreOptionsIndex = displayButtons.findIndex((button) => button.id === 'more-options');
    let moreOptionsButton: HeaderIconButton | undefined;
    if (moreOptionsIndex !== -1) {
      moreOptionsButton = displayButtons.splice(moreOptionsIndex, 1)[0];
    }

    const { length } = displayButtons;
    const orderedButtons = Array.from({ length }, (_, index) => {
      const target = length - 1 - index;
      const foundIndex = displayButtons.findIndex(({ orderIndex }) => orderIndex === target);
      if (foundIndex !== -1) return displayButtons.splice(foundIndex, 1)[0];
      const nextIndex = displayButtons.findIndex(({ orderIndex }) => orderIndex == null);
      return displayButtons.splice(nextIndex, 1)[0];
    });

    if (moreOptionsButton) {
      orderedButtons.push(moreOptionsButton);
    }

    return { displayButtons: orderedButtons, groupedButtons };
  });

  computedHeaderButtons = computed(() => this.allButtons().displayButtons);
  groupedHeaderButtons = computed(() => this.allButtons().groupedButtons);

  @ContentChild('buttons') buttons: TemplateRef<any>;

  onSearchFieldEdit(event: string) {
    this.searchFieldEdit.emit(event);
  }
}
